import { useEffect, useState, useContext, useCallback } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { CheckCircle, Delete, Edit, InsertLink, Star, StarOutline } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download'
import ResourcePreviewPopover from '../ResourcePreviewPopover.js';
import { ResourceStatus, getFileOpenUrl } from '../../data/index.js';
import UserContext from '../../../auth/UserContext.js';
import {default as EventBus} from '../../../utils/EventBus.js'
import Event from '../../../utils/EventNames.js'
import CacheContext from '../../../cache/CacheContext.js';
import { SAFETYRESOURCES } from '../../../users/data/preferences.js';
import { useLocation } from 'react-router-dom';
import { sendFileActivity } from '../../../auth/activity.js';


const FileActionIconButtons = ({file, category, suppressFavorites = false, onDeleteDocument = undefined, onEditResourceProperties = undefined, onApproveDraft = undefined, isManager = false, driveConfig = undefined}) => {
  const driveItem = file?.driveItem ?? file?.DriveItem
  const documentId = driveItem?.id
    // const listItemId = file.eTag.replace(/"/g, '').split(',')[0]
    const status = file?.fields?.Status
    const [copyFade, setCopyFade] = useState(false)
    const [isAddingFavorite, setIsAddingFavorite] = useState(false)
    const [isRemovingFavorite, setIsRemovingFavorite] = useState(false)
    const [favorites, setFavorites] = useState([])

    const userContext = useContext(UserContext)
    const location = useLocation()
    const cache = useContext(CacheContext)
    const FAVORITES_CACHE_KEY = `${SAFETYRESOURCES}Favorites`

    const normalizeIdentity = (value) => String(value ?? '').trim().toLowerCase()

    const currentIdentityKeys = new Set([
      userContext?.email,
      userContext?.userName,
      userContext?.name,
      userContext?.uniqueId,
      userContext?.userProfile?.email,
      userContext?.userProfile?.mail,
      userContext?.userProfile?.userPrincipalName,
      userContext?.userProfile?.displayName,
      userContext?.userProfile?.id,
    ].map(normalizeIdentity).filter(Boolean))

    const fileUserCandidates = [
      file?.createdBy?.user,
      file?.lastModifiedBy?.user,
      driveItem?.createdBy?.user,
      driveItem?.lastModifiedBy?.user,
    ].filter(Boolean)

    const fileIdentityKeys = new Set([
      ...fileUserCandidates.flatMap((u) => [u?.email, u?.userPrincipalName, u?.displayName, u?.id]),
      file?.fields?.AuthorLookupId,
      file?.fields?.EditorLookupId,
    ].map(normalizeIdentity).filter(Boolean))

    const canManageFile = Boolean(isManager)
      || [...currentIdentityKeys].some((key) => fileIdentityKeys.has(key))

    const stopClick = (event) => {
      event?.stopPropagation?.()
    }

    const openFile = async (file) => {
      try {
      const url = await getFileOpenUrl(file)
      if (url) window.open(url, '_blank')
      } catch (error) {
        EventBus.emit(Event.ALERT, { "text": `Error: ${error.message}`, "style": "error", "duration": 5000 })
      }
    }

    const trackFileActivity = (targetFile, activityName) => {
      try {
        sendFileActivity(targetFile, location, activityName)
      } catch (error) {
        // Activity logging should never block user actions.
        console.error('File activity tracking failed:', error?.message || error)
      }
    }

    const resolveResourceId = (_file) => {
      const driveItem = _file?.driveItem ?? _file?.DriveItem
      if (driveItem?.id) return driveItem.id

      const etag = _file?.eTag ?? _file?.fields?.['@odata.etag']
      if (typeof etag === 'string' && etag.length > 0) {
        return etag.replace(/"/g, '').split(',')[0]
      }
      return _file?.listItem?.id ?? _file?.fields?.id ?? _file?.id
    }

    const downloadFile = (_file) => {
      trackFileActivity(_file, 'Download')

      const driveRef = _file?.driveItem ?? _file?.DriveItem
      const url = driveRef?.['@microsoft.graph.downloadUrl'] ?? undefined
      if (url) {
        window.open(url, '_blank')
        return
      }

      // Fallback for responses that omit pre-signed download URLs.
      openFile(_file)
    }

    useEffect(() => {
      if (cache) {
        const favs = cache.getItem(FAVORITES_CACHE_KEY) ?? []
        setFavorites(favs)
      }
    }, [FAVORITES_CACHE_KEY, cache])

    const isFavorite = useCallback((_file) => {
      const _driveItem = _file?.driveItem ?? _file?.DriveItem
      if (!_driveItem?.id) return false
      return (favorites ?? []).find((fav) => 
        typeof fav === "string" ? fav === _driveItem.id : fav?.id === _driveItem.id
      )
    }, [favorites])
    
    const handleAddFavorite = async (_file) => {
      if (!suppressFavorites) {
        if (isAddingFavorite || isRemovingFavorite) {
          return
        }
        trackFileActivity(_file, 'Add Favorite')

        const _driveItem = _file?.driveItem ?? _file?.DriveItem
        if (!_driveItem?.id) {
          EventBus.emit(Event.ALERT, { "text": 'Unable to add favorite: missing file id', "style": "error", "duration": 5000 })
          return
        }
        try {
          setIsAddingFavorite(true)
          const favs = await userContext.saveFavoriteItem(userContext.uniqueId, SAFETYRESOURCES, _driveItem)
          setFavorites(favs)
          // cache.setItem(FAVORITES_CACHE_KEY, favs)
        } catch (error) {
          EventBus.emit(Event.ALERT, { "text": `Unable to add favorite: ${error.message}`, "style": "error", "duration": 5000 })
        } finally {
          setIsAddingFavorite(false)
        }
      }
    }

    const handleRemoveFavorite = async (_file) => {
      if (!suppressFavorites) {
        if (isAddingFavorite || isRemovingFavorite) {
          return
        }
        trackFileActivity(_file, 'Remove Favorite')

        const _driveItem = _file?.driveItem ?? _file?.DriveItem
        if (!_driveItem?.id) {
          EventBus.emit(Event.ALERT, { "text": 'Unable to remove favorite: missing file id', "style": "error", "duration": 5000 })
          return
        }
        try {
          setIsRemovingFavorite(true)
          const favs = await userContext.removeFavoriteItem(userContext.uniqueId, SAFETYRESOURCES, _driveItem)
          // cache.setItem(FAVORITES_CACHE_KEY, favs)
          setFavorites(favs)
        } catch (error) {
          EventBus.emit(Event.ALERT, { "text": `Unable to remove favorite: ${error.message}`, "style": "error", "duration": 5000 })
        } finally {
          setIsRemovingFavorite(false)
        }
      }
    }

    const handleCopyPortalLink = (target, _file) => {
      trackFileActivity(_file, 'Copy Portal Link')

      const resourceId = resolveResourceId(_file)
      if (!resourceId) {
        EventBus.emit(Event.ALERT, { "text": 'Unable to copy link: missing resource id', "style": "error", "duration": 5000 })
        return
      }

      const url = window.location.origin + window.location.pathname
      navigator.clipboard.writeText(`${url}?resourceId=${resourceId}`)
      setCopyFade(true)
      setTimeout(() => setCopyFade(false), 1000)
    }

    const handleEditResourceProperties = (_file) => {
      trackFileActivity(_file, 'Edit Properties')

      const rId = resolveResourceId(_file)
        if (onEditResourceProperties !== undefined) {
          onEditResourceProperties(rId, category)
        }
      }
    
      const handleDeleteDocument = (_file, isPublished) => {
        trackFileActivity(_file, isPublished ? 'Request Delete' : 'Request Delete Draft')

        const rId = resolveResourceId(_file)
        if (onDeleteDocument !== undefined) {
          onDeleteDocument(rId, category, isPublished)
        }
      }
    
      const handleApproveDraft = (_file, _category) => {
        trackFileActivity(_file, 'Approve Draft')

        const rId = resolveResourceId(_file)
        if (onApproveDraft !== undefined) {
          onApproveDraft(rId, _category ?? category)
        }
      }

    // const documentId, openUrl, downloadUrl, status, createdByEmail
    return (
      <>
      { status !== ResourceStatus.DRAFT && 
        <>
        { !suppressFavorites &&
        <>
        { !isFavorite(file) && 
          <Tooltip arrow placement='left' title={`Mark as a Favorite`}>
            <span>
            <IconButton onClick={(e) => { stopClick(e); handleAddFavorite(file) }}><StarOutline /></IconButton>
            </span>
          </Tooltip>
        }
        { isFavorite(file) && 
          <Tooltip arrow placement='left' title={`Remove Favorite`}>
            <span>
            <IconButton onClick={(e) => { stopClick(e); handleRemoveFavorite(file) }}><Star /></IconButton>
            </span>
          </Tooltip>
        }
        </>
        }
        <Tooltip arrow placement='left' title={`Copy a Portal Link to Clipboard`}>
          <IconButton onClick={(e) => { stopClick(e); handleCopyPortalLink(e.target, file) }}
            sx={{
              '& .MuiSvgIcon-root': {
                transition: 'color 0.5s ease-in-out',
                fill: copyFade ? 'lightgreen' : 'currentColor',
              },
            }}><InsertLink /></IconButton>
          </Tooltip>
        </>
        
      }
      { canManageFile
        && <Tooltip arrow placement='left' title={`Edit File Properties`}>
          <span>
            <IconButton onClick={(e) => { stopClick(e); handleEditResourceProperties(file) }}><Edit /></IconButton>
          </span>
        </Tooltip>
       }
      <ResourcePreviewPopover file={file} key={file?.driveItem?.id} triggerLabel='Open a Preview' driveConfig={driveConfig} />
      <Tooltip arrow placement='left' title={`Download File`}>
        <IconButton onClick={(e) => { stopClick(e); downloadFile(file) }}><DownloadIcon /></IconButton>
      </Tooltip>

      {isManager 
        && <>
          {status === ResourceStatus.DRAFT
            && <Tooltip arrow placement='left' title='Approve and Publish'>
                <IconButton onClick={(e) => { stopClick(e); handleApproveDraft(file) }}><CheckCircle color='success'/></IconButton>
              </Tooltip>
          }
          {status !== ResourceStatus.DRAFT
            && <Tooltip arrow placement='left' title='Delete File'>
                  <IconButton onClick={(e) => { stopClick(e); handleDeleteDocument(file) }}><Delete color='error'/></IconButton>
                </Tooltip>
          }
        </>
      }
      {status === ResourceStatus.DRAFT
        && <Tooltip arrow placement='left' title='Delete File'>
            <IconButton onClick={(e) => { stopClick(e); handleDeleteDocument(file) }}><Delete color='error'/></IconButton>
          </Tooltip>
      }
      </>
    )
  }

export default FileActionIconButtons